import { useEffect } from 'react';
import { Outlet, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import CookieConsent from '../components/CookieConsent';
import { languagesList } from '../i18n';

export default function Layout() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const currentLang = lang || 'en';

  useEffect(() => {
    if (!lang) {
      i18n.changeLanguage('en');
    } else if (languagesList.find(l => l.code === lang)) {
      i18n.changeLanguage(lang);
    } else {
      navigate('/', { replace: true });
    }
  }, [lang, i18n, navigate]);

  // Calculate relative path for subpages
  let relPath = location.pathname;
  if (lang && relPath.startsWith(`/${lang}`)) {
    relPath = relPath.slice(lang.length + 1); // +1 for the slash
  }
  if (relPath === '/') relPath = '';

  const getCanonicalUrl = (languageCode: string) => {
    const base = 'https://free-barcode-generator.online';
    if (languageCode === 'en') {
      return `${base}${relPath}`;
    }
    return `${base}/${languageCode}${relPath}`;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans overflow-x-hidden flex flex-col items-center">
      <Helmet>
        <html lang={currentLang} />
        <title>{t('meta_title_home')}</title>
        <meta name="description" content={t('meta_desc_home')} />
        {languagesList.map(l => (
          <link key={l.code} rel="alternate" hrefLang={l.code} href={getCanonicalUrl(l.code)} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={getCanonicalUrl('en')} />
        <link rel="canonical" href={getCanonicalUrl(currentLang)} />
        
        {/* Social Media Meta */}
        <meta property="og:title" content={t('meta_title_home')} />
        <meta property="og:description" content={t('meta_desc_home')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCanonicalUrl(currentLang)} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta_title_home')} />
        <meta name="twitter:description" content={t('meta_desc_home')} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "${t('meta_title_home')}",
              "url": "${getCanonicalUrl(currentLang)}",
              "description": "${t('meta_desc_home')}",
              "applicationCategory": "UtilitiesApplication",
              "operatingSystem": "All",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            }
          `}
        </script>
      </Helmet>

      {/* Header Navigation */}
      <nav className="h-16 w-full border-b border-white/10 flex items-center justify-between px-4 sm:px-8 bg-slate-900/50 backdrop-blur-xl shrink-0 sticky top-0 z-50">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(currentLang === 'en' ? '/' : `/${currentLang}/`)}>
          <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center font-bold text-xl">B</div>
          <span className="text-xl font-semibold tracking-tight hidden sm:block">{t('app_title')}</span>
        </div>
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="group relative">
            <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full border border-white/10 cursor-pointer hover:bg-white/20">
              <span className="text-xs font-medium uppercase">{currentLang}</span>
              <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
            
            {/* Language Dropdown */}
            <div className="absolute right-0 mt-2 w-56 sm:w-80 bg-slate-800 border border-white/10 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all grid grid-cols-2 p-2 max-h-[60vh] overflow-y-auto z-50">
                {languagesList.map(l => (
                  <button 
                     key={l.code}
                     onClick={() => {
                        const base = l.code === 'en' ? '' : `/${l.code}`;
                        navigate(`${base}${relPath}` || '/');
                     }}
                     className={`text-left px-3 py-2 text-xs rounded hover:bg-white/10 ${currentLang === l.code ? 'bg-indigo-600/30 text-indigo-300' : 'text-white/80'}`}
                  >
                    {l.label} ({l.code})
                  </button>
                ))}
            </div>
          </div>
        </div>
      </nav>

      <Outlet />

      {/* Sticky Bottom Trust Bar (E-E-A-T) */}
      <footer className="w-full py-6 bg-slate-950 border-t border-white/10 flex flex-col md:flex-row items-center justify-between px-4 sm:px-8 text-xs text-white/50 shrink-0 gap-4 mt-auto">
        <div className="flex items-center text-center sm:text-left">
          <span>&copy; {new Date().getFullYear()} {t('barcode_generator')}</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to={currentLang === 'en' ? '/terms' : `/${currentLang}/terms`} className="hover:text-white transition-colors">{t('terms_of_use')}</Link>
          <Link to={currentLang === 'en' ? '/privacy' : `/${currentLang}/privacy`} className="hover:text-white transition-colors">{t('privacy_policy')}</Link>
          <Link to={currentLang === 'en' ? '/mobile' : `/${currentLang}/mobile`} className="hover:text-white transition-colors">{t('mobile_app')}</Link>
          <Link to={currentLang === 'en' ? '/contacts' : `/${currentLang}/contacts`} className="hover:text-white transition-colors">{t('contacts')}</Link>
          <Link to={currentLang === 'en' ? '/sitemap' : `/${currentLang}/sitemap`} className="hover:text-white transition-colors">{t('site_map')}</Link>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}
